import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { useApi, useDebounced, useMutation } from '../lib/use-api';
import { formatDateTime } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { UserDialog } from '../components/UserDialog';
import { identityFetch } from '../api/identity';

const PAGE_SIZE = 25;

export interface AdminUser {
  principalId: string;
  email: string | null;
  displayName: string;
  /** NULL với người dùng LTI/nhúng: không có tài khoản đăng nhập để khoá. */
  status: string | null;
  source: 'local' | 'lti' | 'embed';
  roles: string[];
  createdAt: string;
}

/**
 * Quản trị người dùng — plan §5.3, §6.2.
 *
 * Gọi service `identity` chứ không delivery-api: tài khoản thuộc về identity,
 * và trộn hai đường vào một client làm mờ ranh giới ai sở hữu dữ liệu gì.
 */
export function UsersPage(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';

  const [keyword, setKeyword] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<AdminUser | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  const debouncedKeyword = useDebounced(keyword);

  const users = useApi<{ items: AdminUser[]; total: number; limit: number; offset: number }>(
    '/api/admin/users',
    { query: { q: debouncedKeyword, limit: PAGE_SIZE, offset }, identity: true },
  );

  const setStatus = useMutation<{ principalId: string; status: string }, void>(
    useCallback(
      (input: { principalId: string; status: string }) =>
        identityFetch<void>(`/api/admin/users/${input.principalId}/status`, {
          method: 'PUT',
          body: { status: input.status },
        }),
      [],
    ),
  );

  const toggleStatus = async (user: AdminUser): Promise<void> => {
    const next = user.status === 'active' ? 'disabled' : 'active';
    const done = await setStatus.run({ principalId: user.principalId, status: next });
    if (done !== undefined) users.reload();
  };

  return (
    <section>
      <h1>{t('users.heading')}</h1>

      <div className="toolbar">
        <label className="search-field">
          <span className="visually-hidden">{t('common:action.search')}</span>
          <input
            type="search"
            value={keyword}
            placeholder={t('users.searchPlaceholder')}
            onChange={(event) => {
              setKeyword(event.target.value);
              setOffset(0);
            }}
          />
        </label>
        <button type="button" onClick={() => setCreating(true)}>
          {t('users.create')}
        </button>
      </div>

      {users.loading ? <Loading /> : null}
      {users.error ? <ErrorNotice error={users.error} onRetry={users.reload} /> : null}
      {setStatus.error ? <ErrorNotice error={setStatus.error} /> : null}

      {users.data && !users.loading ? (
        users.data.items.length === 0 ? (
          <EmptyNotice message={t('users.empty')} />
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('users.column.displayName')}</th>
                  <th scope="col">{t('users.column.email')}</th>
                  <th scope="col">{t('users.column.roles')}</th>
                  <th scope="col">{t('users.column.source')}</th>
                  <th scope="col">{t('users.column.status')}</th>
                  <th scope="col">{t('users.column.createdAt')}</th>
                  <th scope="col">{t('library.column.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.data.items.map((user) => (
                  <tr key={user.principalId}>
                    <td>{user.displayName}</td>
                    <td>{user.email ?? '—'}</td>
                    <td>
                      {user.roles.map((role) => (
                        <span key={role} className="chip chip--static">
                          {t(`users.role.${role}`, { defaultValue: role })}
                        </span>
                      ))}
                    </td>
                    <td>{t(`users.source.${user.source}`)}</td>
                    <td>
                      {user.status
                        ? t(`users.status.${user.status}`, { defaultValue: user.status })
                        : '—'}
                    </td>
                    <td>{formatDateTime(user.createdAt, locale)}</td>
                    <td className="actions">
                      <button type="button" onClick={() => setEditing(user)}>
                        {t('users.editRoles')}
                      </button>
                      {user.source === 'local' ? (
                        <button type="button" onClick={() => void toggleStatus(user)}>
                          {user.status === 'active'
                            ? t('common:action.disable')
                            : t('common:action.enable')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              total={users.data.total}
              limit={users.data.limit}
              offset={users.data.offset}
              onChange={setOffset}
            />
          </>
        )
      ) : null}

      {creating ? (
        <UserDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            users.reload();
          }}
        />
      ) : null}

      {editing ? (
        <UserDialog
          user={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            users.reload();
          }}
        />
      ) : null}
    </section>
  );
}

export type { ApiError };
